import React, { Fragment, useRef } from 'react';
import { useNavigate } from "@remix-run/react";
import { ChannelPrograms } from './ChannelPrograms/ChannelPrograms';
import { ChannelSelector } from './ChannelSelector/ChannelSelector';
import { useSearchParams } from '../../../../play-core/hooks/use-search-params';
import { useTvGuideDate } from '../../components/DateSelection/use-tv-guide-date';
import { useLoadingBar } from '../../components/LoadingBar/LoadingBarProvider';
import { TvGuideMessage } from '../../components/TvGuide/TvGuideMessage';
import { TvGuideDateSelection } from '../../components/TvGuideDateSelection/TvGuideDateSelection';
import { usePath, useVideoConfig } from '../../config/VideoAppContext';
import { useTvGuide } from '../../hooks/use-tv-guide';
import { useTvGuideChannel } from '../../hooks/use-tv-guide-channel';
import { isoDate } from '../../utils/date-formatter';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const TvGuideChannelPage = ({
  initialData
}) => {
  const {
    tvGuideByChannelPath
  } = usePath();
  const {
    businessUnit,
    tvGuide: {
      tvGuideProviders = [businessUnit]
    } = {}
  } = useVideoConfig();
  const navigate = useNavigate();
  const {
    date,
    setDate
  } = useTvGuideDate();
  const {
    programs = [],
    isLoading: isLoadingChannels,
    isError: isErrorChannels
  } = useTvGuide(isoDate(date), tvGuideProviders, initialData.tvGuide);
  const channels = programs.map(program => program.channel);
  const {
    channelUrn = ''
  } = useSearchParams();
  const activeChannel = channels.find(channel => channel.urn === channelUrn) || (isLoadingChannels ? undefined : channels[0]);
  const {
    extendedProgram,
    isLoading: isLoadingDetails,
    isError: isErrorDetails
  } = useTvGuideChannel(isoDate(date), activeChannel);
  const isEmpty = channels.length === 0;
  const isLoading = isLoadingChannels || isLoadingDetails;
  const isError = isErrorChannels || isErrorDetails;
  const showMessage = (isEmpty || isError) && !isLoading;

  /**
   * Memoize channelList until new are loaded
   * This allows TvGuideDateSelection changes for the same channel even if the new program is loading and activeChannel is not set yet.
   * This allows channel selection even if the data are still loading
   */
  const memoizeChannelListRef = useRef(channels);
  if (!isLoadingChannels && !isEmpty || memoizeChannelListRef.current.length === 0) {
    memoizeChannelListRef.current = channels;
  }
  const memoizeActiveChannel = memoizeChannelListRef.current.find(channel => channel.urn === channelUrn) || (isLoadingChannels ? undefined : channels[0]);
  useLoadingBar(isLoading);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(TvGuideDateSelection, {
      date: date,
      onDateChange: value => memoizeActiveChannel && navigate(tvGuideByChannelPath(isoDate(value), memoizeActiveChannel)),
      onInstantChange: setDate,
      analyticsPageId: 'tvGuideByChannel'
    }), showMessage ? /*#__PURE__*/_jsx(TvGuideMessage, {
      isLoading: isLoading,
      isError: isError,
      isEmpty: isEmpty
    }) : /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(ChannelSelector, {
        channels: memoizeChannelListRef.current.length === 0 ? channels : memoizeChannelListRef.current,
        activeChannelId: memoizeActiveChannel?.id,
        onChannelSelect: channel => {
          channel && navigate(tvGuideByChannelPath(isoDate(date), channel));
        }
      }), /*#__PURE__*/_jsx(ChannelPrograms, {
        programGuide: extendedProgram,
        isLoading: isLoading
      })]
    })]
  });
};